import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import { ConfigContext } from '../../shared/ConfigContext';
import {
  fetchFeatures, fetchPlans, createPlan, archivePlan, restorePlan, setDefaultPlan, clearDefaultPlan, setPlanOrder,
  fetchGrants, fetchBillingConfig, reconcileBilling,
} from '../../../api/planClient';
import { inputToCents } from './planUtils';

export const MAX_ACTIVE_PLANS = 5;
const EMPTY_FORM = { name: '', description: '', price: '0.00', currency: 'USD' };

const mapError = (t, err) => {
  if (err.response?.status === 401) return t('ga.error.unauthorized');
  if (err.response?.status === 403) return t('ga.error.forbidden');
  if (err.response?.status === 400 || err.response?.status === 409) return err.response?.data?.error || t('ga.error.generic');
  if (err.response?.status >= 500) return err.response?.data?.error || t('ga.error.server');
  if (!err.response) return t('ga.error.network');
  return t('ga.error.generic');
};

const useGAPlansPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [grants, setGrants] = useState([]);
  const [billing, setBilling] = useState({ stripeEnabled: false });
  const [reconcileReport, setReconcileReport] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'archive'|'default', plan }
  const [busy, setBusy] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    if (!config?.backendUrl) return;
    setStatus('loading');
    // Grants and billing config are additive: if those calls fail the catalog still renders.
    const grantsReq = fetchGrants(config.backendUrl).catch(() => ({ data: { items: [] } }));
    const billingReq = fetchBillingConfig(config.backendUrl).catch(() => ({ data: { stripeEnabled: false } }));
    Promise.all([fetchFeatures(config.backendUrl), fetchPlans(config.backendUrl), grantsReq, billingReq])
      .then(([f, p, gr, b]) => {
        setFeatures(f.data);
        setPlans(p.data);
        setGrants(Array.isArray(gr.data?.items) ? gr.data.items : []);
        setBilling({ stripeEnabled: false, ...(b.data || {}) });
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error loading plans', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({ request: `GET /ga/plans · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}` });
          setStatus('error');
        }
      });
  }, [config, t]);

  useEffect(() => { load(); }, [load]);

  const featuresByKey = useMemo(() => Object.fromEntries(features.map((f) => [f.key, f])), [features]);
  const activePlans = useMemo(() => plans.filter((p) => p.status !== 'archived'), [plans]);
  // The pricing row, the compare matrix and the cap only concern public
  // plans; operator-only plans live in their own section below.
  const publicPlans = useMemo(() => activePlans.filter((p) => p.visibility !== 'private'), [activePlans]);
  const privatePlans = useMemo(() => activePlans.filter((p) => p.visibility === 'private'), [activePlans]);
  const archivedPlans = useMemo(() => plans.filter((p) => p.status === 'archived'), [plans]);
  const canCreate = publicPlans.length < MAX_ACTIVE_PLANS;

  const formErrors = useMemo(() => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('ga.validation.required');
    if (form.name.trim().length > 40) errs.name = t('At most 40 characters', { defaultValue: 'At most 40 characters' });
    if (inputToCents(form.price) < 0 || Number.isNaN(Number.parseFloat(form.price))) errs.price = t('Enter a price of 0 or more', { defaultValue: 'Enter a price of 0 or more' });
    return errs;
  }, [form, t]);
  const isFormValid = Object.keys(formErrors).length === 0;

  const openCreate = () => { setForm(EMPTY_FORM); setDrawerOpen(true); };
  const closeCreate = () => { if (!saving) setDrawerOpen(false); };

  const handleCreate = async () => {
    if (!isFormValid || saving) return;
    setSaving(true);
    try {
      const res = await createPlan(config.backendUrl, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: { amount: inputToCents(form.price), currency: form.currency, interval: 'month' },
        entitlements: [],
      });
      notify(t('Plan created — now add its features', { defaultValue: 'Plan created — now add its features' }));
      setDrawerOpen(false);
      navigate(`/ga/plans/${res.data._id}`);
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      notify(successMessage);
      load();
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const confirmAction = () => {
    if (!confirm) return;
    const { kind, plan } = confirm;
    if (kind === 'archive') return runAction(() => archivePlan(config.backendUrl, plan._id), t('{{name}} archived', { name: plan.name, defaultValue: `${plan.name} archived` }));
    if (kind === 'default') return runAction(() => setDefaultPlan(config.backendUrl, plan._id), t('{{name}} now carries the Recommended badge', { name: plan.name, defaultValue: `${plan.name} now carries the Recommended badge` }));
    if (kind === 'clear_default') return runAction(() => clearDefaultPlan(config.backendUrl), t('Recommended badge removed', { defaultValue: 'Recommended badge removed' }));
    if (kind === 'reconcile_fix') return runAction(async () => { const res = await reconcileBilling(config.backendUrl, true); setReconcileReport(res.data); }, t('Stripe reconciliation applied', { defaultValue: 'Stripe reconciliation applied' }));
    return undefined;
  };

  // Dry-run first; the "apply" step goes through the confirm dialog.
  const reconcile = async () => {
    setBusy(true);
    try {
      const res = await reconcileBilling(config.backendUrl, false);
      setReconcileReport(res.data);
      if (!res.data.drift?.length) {
        notify(t('Stripe and SmartInventory agree on all {{count}} subscriptions', { count: res.data.checked, defaultValue: `Stripe and SmartInventory agree on all ${res.data.checked} subscriptions` }));
      } else {
        setConfirm({ kind: 'reconcile_fix', report: res.data });
      }
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const restore = (plan) => runAction(() => restorePlan(config.backendUrl, plan._id), t('{{name}} restored', { name: plan.name, defaultValue: `${plan.name} restored` }));

  // Optimistic reorder, persisted with one PUT; reverted on failure.
  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = publicPlans.map((p) => p._id);
    const reordered = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    const before = plans;
    setPlans((prev) => [...reordered.map((id) => prev.find((p) => p._id === id)), ...privatePlans, ...archivedPlans]);
    try {
      await setPlanOrder(config.backendUrl, [...reordered, ...privatePlans.map((p) => p._id), ...archivedPlans.map((p) => p._id)]);
    } catch (error) {
      setPlans(before);
      notify(mapError(t, error), 'error');
    }
  };

  return {
    t, status, errorInfo, load, features, featuresByKey, plans, activePlans, publicPlans, privatePlans, archivedPlans, grants, canCreate,
    billing, reconcile, reconcileReport,
    drawerOpen, openCreate, closeCreate, form, setForm, formErrors, isFormValid, saving, handleCreate,
    confirm, setConfirm, confirmAction, busy, restore, handleDragEnd,
    goToBuilder: (plan) => navigate(`/ga/plans/${plan._id}`),
    snackbar, setSnackbar,
  };
};

export default useGAPlansPage;
